import { useQuery } from "@tanstack/react-query";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { fetchHolidaySet } from "@/hooks/use-company-holidays";
import {
  buildPayrollBasis,
  type PayrollBasisEmployee,
  type PayrollEntry,
} from "@/lib/overtime-calc";

interface RawEntryRow {
  id: string;
  user_id: string | null;
  start_date: string;
  task_description: string;
  weekend_holiday_hours: number | null;
  overnight_count: number | null;
  users: { full_name: string | null } | null;
  projects: { name: string | null } | null;
}

/**
 * Non-hook fetch for lønnsgrunnlaget (VIB-1224) — delt av rapport-resolveren
 * og CSV-eksporten (resolvere er ikke React-hooks). Team-bred lesing av
 * time_entries → fetchAllPaginated (1000-rads-cappen, VIB-946/1249).
 * Månedsattribusjon på start_date, aldri created_at. Kun føringer med
 * flaggede tilleggstimer eller overnattinger; soft-deleted er ute.
 */
export async function fetchPayrollBasis(period: {
  from: string;
  to: string;
}): Promise<PayrollBasisEmployee[]> {
  // `to` er inklusiv sluttdato; start_date kan være timestamp → bruk < dagen etter.
  const toExclusive = `${period.to}T23:59:59.999`;
  const [entries, holidays] = await Promise.all([
    fetchAllPaginated<RawEntryRow>(
      "time_entries",
      "id, user_id, start_date, task_description, weekend_holiday_hours, overnight_count, users:user_id(full_name), projects:project_id(name)",
      (q) =>
        q
          .gte("start_date", period.from)
          .lte("start_date", toExclusive)
          .is("deleted_at", null)
          .or("weekend_holiday_hours.gt.0,overnight_count.gt.0"),
    ),
    fetchHolidaySet(period.from, period.to),
  ]);

  const mapped: PayrollEntry[] = entries.map((e) => ({
    id: e.id,
    user_id: e.user_id,
    full_name: e.users?.full_name ?? null,
    start_date: e.start_date,
    project_name: e.projects?.name ?? null,
    task_description: e.task_description,
    weekend_holiday_hours: e.weekend_holiday_hours,
    overnight_count: e.overnight_count,
  }));
  return buildPayrollBasis(mapped, holidays);
}

/** Lønnsgrunnlag per ansatt for en periode (rapporten under /rapporter). */
export function usePayrollBasis(period: { from: string; to: string }) {
  return useQuery({
    queryKey: ["payroll-basis", { from: period.from, to: period.to }],
    staleTime: 5 * 60 * 1000,
    queryFn: () => fetchPayrollBasis(period),
  });
}
